import './auto-credit.scss';

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import {
  AdaptiveAutoCredit,
  AutoCreditHeader,
  FilterModal
} from '@/components/App/AutoCred/entities';
import {
  ClientOtpModal,
  SystemNotifications
} from '@/components/Shared/entities';
import { ROUTES } from '@/components/Shared/routes/routes';
import { Button, Input, Table } from '@/components/Shared/ui';
import { DictTypes } from '@/constants';
import FilterIcon from '@/media/icons/filter.svg?react';
import SearchIcon from '@/media/icons/search.svg?react';
import { useAppDispatch, useAppSelector } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';
import type { TDataSource } from '@/types/dataSource';

import useRequests from './hooks/useRequests';
import useValidateAutoCreditOtp from './hooks/useValidateAutoCreditOtp';
import { autoCreditTableColumns } from './widgets';

const AutoCredit = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { dicts } = useAppSelector((state) => state.dict);
  const {
    userSelectOptions,
    dealerOptions,
    bankOptions,
    filteredArray,
    totalRequests,
    isTableDataLoading,
    filters: {
      autoTypes,
      activeAutoType,
      paginationData,
      searchValue,
      handleFiltersChange,
      handleSearch,
      handleApplyFilter,
      filterParamValues,
      selectedUser
    },
    getRequestsPerPage
  } = useRequests();
  const {
    onClientSubmit,
    onOtpSubmit,
    retryOTP,
    continueVerifying,
    isValidateModalOpened,
    setIsValidateModalOpened
  } = useValidateAutoCreditOtp({ getRequestsPerPage });

  const [searchParams, setSearchParams] = useSearchParams();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeStatusFilter, setActiveStatusFilter] = useState<string[]>([]);

  const handleRowClicked = async (row: TDataSource) => {
    if (row.status.isError) return;
    if (row.status.isUnverified) continueVerifying(row);
    else if (row.status.isDraft)
      navigate(ROUTES.autoCreditCreate, {
        state: { requestId: row.id }
      });
    else
      navigate(ROUTES.autoCreditRequest(row.id), {
        state: { requestId: row.id }
      });
  };

  const handlePageChange = (currentPage: number, itemsPerPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(currentPage));
    params.set('per-page', String(itemsPerPage));
    setSearchParams(params);
  };

  const handleOpenFilterModal = () => {
    setIsModalOpen(true);
  };

  const onCloseFilterModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    setActiveStatusFilter(
      filterParamValues.activeParamStatuses.map((item) => item.value)
    );
  }, [searchParams]);

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: 'Автокредитование',
        helmetTitle: 'Автокредитование'
      })
    );
    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, []);

  return (
    <>
      <SystemNotifications />

      <div className="auto-credit">
        <AutoCreditHeader
          onCreateClick={() =>
            setIsValidateModalOpened({ openedModal: 'client-info-form' })
          }
        />

        <div className="auto-credit__filters">
          <div className="auto-credit__search">
            <Input
              placeholder="Поиск по ФИО / ИИН / Номер тел."
              value={searchValue}
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
              endContent={
                <span className="auto-credit__search-icon">
                  <SearchIcon />
                </span>
              }
            />
          </div>
          <div className="auto-credit__item">
            <Button
              onClick={handleOpenFilterModal}
              isSquare
              className="auto-credit__item-button"
            >
              <FilterIcon />
            </Button>
          </div>
        </div>
        <div className="auto-credit__table">
          <Table
            columns={autoCreditTableColumns({
              statusesFilters: activeStatusFilter,
              banksDict: dicts?.[DictTypes.Banks]
            })}
            dataSource={filteredArray}
            rowKey={(record) => record?.id || ''}
            rowLink={(item) =>
              !item.status.isDraft &&
              !item.status.isUnverified &&
              !item.status.isError
                ? ROUTES.autoCreditRequest(item.id)
                : undefined
            }
            pagination={{
              total: totalRequests,
              pageSize: paginationData.itemsPerPage,
              current: paginationData.currentPage,
              onChange: handlePageChange
            }}
            onChange={handleFiltersChange}
            loading={isTableDataLoading}
            onRowClick={handleRowClicked}
          />
        </div>
        <div className="auto-credit__mobile-data">
          <AdaptiveAutoCredit
            data={filteredArray}
            pagination={{
              current: paginationData.currentPage,
              pageSize: paginationData.itemsPerPage,
              total: totalRequests
            }}
            onPageChange={handlePageChange}
            onClick={handleRowClicked}
            isLoading={isTableDataLoading}
          />
        </div>
      </div>

      <ClientOtpModal
        title="Создание заявки"
        subtitle="Заполните данные о клиенте"
        onClientSubmit={onClientSubmit}
        onOtpSubmit={onOtpSubmit}
        onSMSRetry={(phone) => retryOTP({ phone, type: 'SMS' })}
        openedModal={isValidateModalOpened.openedModal}
        setOpenedModal={(modal) =>
          setIsValidateModalOpened({ openedModal: modal })
        }
        onClose={() => setIsValidateModalOpened({ openedModal: '' })}
        defaultPhone={isValidateModalOpened.defaultPhone}
      />

      <FilterModal
        selectedUser={selectedUser}
        isOpen={isModalOpen}
        onClose={onCloseFilterModal}
        autoTypes={autoTypes}
        activeAutoType={activeAutoType}
        userSelectOptions={userSelectOptions}
        dealerOptions={dealerOptions}
        bankOptions={bankOptions}
        filterParamValues={filterParamValues}
        handleApplyFilter={handleApplyFilter}
      />
    </>
  );
};

export default AutoCredit;
